import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AdminOverviewStats } from "../../types/adminStats.definition";
import { CHART_COLORS, monthLabel } from "../../utils/chart-utils";
import { formatMoney } from "../../utils/money-utils";

/** Licence statuses in lifecycle order; unknown statuses follow. */
const LICENCE_STATUS_ORDER = [
  "ACTIVE",
  "TRIAL",
  "PENDING_PAYMENT",
  "PAUSED",
  "EXPIRED",
  "CANCELLED",
];

const INVOICE_STATUS_COLORS: Record<string, string> = {
  PAID: CHART_COLORS.green,
  PENDING: CHART_COLORS.amber,
  SENT: CHART_COLORS.sky,
  OVERDUE: CHART_COLORS.rose,
  CANCELLED: CHART_COLORS.slate,
};

const ANIMATION_MS = 900;

interface ChartCardProps {
  title: string;
  subtitle: string;
  isEmpty: boolean;
  emptyLabel: string;
  /** Screen-reader version of the chart data. */
  summary: string[];
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  subtitle,
  isEmpty,
  emptyLabel,
  summary,
  children,
}) => (
  // relative: keeps the absolutely positioned sr-only list inside the card, so it
  // cannot stretch the document and scroll the whole window
  <section className="relative card-container" aria-label={title}>
    <h3 className="text-lg font-medium text-neutral-900">{title}</h3>
    <p className="mb-4 text-sm text-neutral-500">{subtitle}</p>
    {isEmpty ? (
      <p className="flex h-64 items-center justify-center text-sm text-neutral-500">
        {emptyLabel}
      </p>
    ) : (
      <>
        <div className="h-64">{children}</div>
        <ul className="sr-only" data-testid="chart-data">
          {summary.map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </>
    )}
  </section>
);

interface DashboardInsightsProps {
  stats: AdminOverviewStats;
}

/**
 * MED-1333: animated charts under the dashboard cards — platform activity, licence health,
 * invoice collection and the user base.
 */
export const DashboardInsights: React.FC<DashboardInsightsProps> = ({
  stats,
}) => {
  const { t, i18n } = useTranslation();
  const noData = t("admin.insights.noData");

  const appointments = stats.monthlyAppointments.map(item => ({
    label: monthLabel(item.month, i18n.language),
    count: item.count,
  }));

  const licenceStatuses = Array.from(
    new Set([
      ...Object.keys(stats.facilityLicencesByStatus),
      ...Object.keys(stats.doctorLicencesByStatus),
    ])
  ).sort((a, b) => {
    const rank = (status: string) => {
      const index = LICENCE_STATUS_ORDER.indexOf(status);
      return index === -1 ? LICENCE_STATUS_ORDER.length : index;
    };
    return rank(a) - rank(b) || a.localeCompare(b);
  });
  const licences = licenceStatuses.map(status => ({
    label: t(`admin.invoices.values.${status}`, { defaultValue: status }),
    facilities: stats.facilityLicencesByStatus[status] ?? 0,
    doctors: stats.doctorLicencesByStatus[status] ?? 0,
  }));

  const invoiceStatusLabel = (status: string) =>
    t(`admin.invoices.status.${status.toLowerCase()}`, {
      defaultValue: status,
    });
  const invoices = stats.invoicesByStatus.map(item => ({
    status: item.status,
    name: invoiceStatusLabel(item.status),
    value: item.count,
    amount: item.amount,
  }));
  const invoiceTotal = invoices.reduce((sum, item) => sum + item.value, 0);

  const users = Object.entries(stats.usersByRole)
    .map(([role, count]) => ({
      label: t(`admin.insights.roles.${role}`, { defaultValue: role }),
      count,
    }))
    .sort((a, b) => b.count - a.count);

  return (
    <div className="page-section">
      <h2 className="mb-4 text-lg font-medium text-neutral-900">
        {t("admin.insights.title")}
      </h2>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title={t("admin.insights.appointments.title")}
          subtitle={t("admin.insights.appointments.subtitle")}
          isEmpty={appointments.every(item => item.count === 0)}
          emptyLabel={noData}
          summary={appointments.map(item => `${item.label}: ${item.count}`)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={appointments}
              margin={{ top: 8, right: 16, bottom: 0, left: -16 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} fontSize={12} />
              <YAxis allowDecimals={false} tickLine={false} fontSize={12} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                name={t("admin.insights.appointments.series")}
                stroke={CHART_COLORS.primary}
                strokeWidth={3}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                animationDuration={ANIMATION_MS}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={t("admin.insights.licences.title")}
          subtitle={t("admin.insights.licences.subtitle")}
          isEmpty={licences.length === 0}
          emptyLabel={noData}
          summary={licences.map(
            item =>
              `${item.label}: ${item.facilities} ${t("admin.insights.licences.facilities")}, ${item.doctors} ${t("admin.insights.licences.doctors")}`
          )}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={licences}
              margin={{ top: 8, right: 16, bottom: 0, left: -16 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tickLine={false} fontSize={12} />
              <YAxis allowDecimals={false} tickLine={false} fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="facilities"
                stackId="licences"
                name={t("admin.insights.licences.facilities")}
                fill={CHART_COLORS.primary}
                animationDuration={ANIMATION_MS}
              />
              <Bar
                dataKey="doctors"
                stackId="licences"
                name={t("admin.insights.licences.doctors")}
                fill={CHART_COLORS.secondary}
                radius={[4, 4, 0, 0]}
                animationDuration={ANIMATION_MS}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={t("admin.insights.invoices.title")}
          subtitle={t("admin.insights.invoices.subtitle")}
          isEmpty={invoiceTotal === 0}
          emptyLabel={noData}
          summary={invoices.map(
            item => `${item.name}: ${item.value}, ${formatMoney(item.amount)}`
          )}
        >
          <div className="flex h-full items-center gap-4">
            <div className="relative h-full flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={invoices}
                    dataKey="value"
                    nameKey="name"
                    innerRadius="60%"
                    outerRadius="90%"
                    paddingAngle={2}
                    animationDuration={ANIMATION_MS}
                  >
                    {invoices.map(item => (
                      <Cell
                        key={item.status}
                        fill={
                          INVOICE_STATUS_COLORS[item.status] ??
                          CHART_COLORS.violet
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-semibold text-neutral-900">
                  {invoiceTotal}
                </span>
                <span className="text-xs text-neutral-500">
                  {t("admin.insights.invoices.total")}
                </span>
              </div>
            </div>
            <ul className="space-y-2 text-sm" aria-hidden="true">
              {invoices.map(item => (
                <li key={item.status} className="flex items-start gap-2">
                  <span
                    className="mt-1 inline-block h-3 w-3 rounded-full"
                    style={{
                      backgroundColor:
                        INVOICE_STATUS_COLORS[item.status] ??
                        CHART_COLORS.violet,
                    }}
                  />
                  <span>
                    <span className="font-medium text-neutral-900">
                      {item.name}
                    </span>{" "}
                    <span className="text-neutral-600">{item.value}</span>
                    <span className="block text-neutral-500">
                      {formatMoney(item.amount)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </ChartCard>

        <ChartCard
          title={t("admin.insights.users.title")}
          subtitle={t("admin.insights.users.subtitle")}
          isEmpty={users.length === 0}
          emptyLabel={noData}
          summary={users.map(item => `${item.label}: ${item.count}`)}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={users}
              layout="vertical"
              margin={{ top: 8, right: 24, bottom: 0, left: 16 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tickLine={false}
                fontSize={12}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={110}
                tickLine={false}
                fontSize={12}
              />
              <Tooltip />
              <Bar
                dataKey="count"
                name={t("admin.insights.users.series")}
                fill={CHART_COLORS.secondary}
                radius={[0, 4, 4, 0]}
                animationDuration={ANIMATION_MS}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};
